import type { Segment } from '@pigello/pigello-matrix';
import { fetchAllInstances } from '@/requests/api/fetch-all-instances';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { tryCatch } from '@/utils/try-catch';
import { api, fetchApiQuery, query } from '../api';
import { readFromLocalStorage } from '../local-storage/local-storage';
import { ObjectKeys } from '../utils';
import {
  GLOBAL_FILTER_DEFAULT_VALUES,
  GLOBAL_FILTER_KEY,
  QUERYABLE_FILTER_KEYS,
} from './constants';
import type { GlobalFilter } from './types';

/**
 * Fetches real estate IDs from external clients by first getting their companies,
 * then getting real estates owned by those companies
 */
async function fetchRealEstatesFromExternalClients(
  externalClientIds: string[]
): Promise<string[]> {
  const { data: companyIds = [], error: companyError } = await tryCatch(
    async () =>
      await fetchApiQuery(
        query(() =>
          api.organization_company.query.ids.queryOptions({
            params: {
              slim: true,
              ids: externalClientIds,
              attribute: 'managedBy',
            },
          })
        ).select((item) => item.id)
      )
  );

  if (!companyIds?.length || companyError) {
    return [];
  }

  const { data: realEstateIds, error: realestateCompanyError } = await tryCatch(
    async () => {
      return (
        (await fetchApiQuery(
          query(() =>
            api.objects_realestate.query.ids.queryOptions({
              params: {
                slim: true,
                ids: companyIds,
                attribute: 'ownedBy',
              },
            })
          ).select((item) => item.id)
        )) ?? []
      );
    }
  );

  if (!realEstateIds?.length || realestateCompanyError) {
    return [];
  }

  return realEstateIds;
}

/**
 * Fetches real estate IDs directly from company IDs
 */
async function fetchRealEstatesFromCompanies(
  companyIds: string[]
): Promise<string[]> {
  const { data: realEstateIds, error: realestateError } = await tryCatch(
    async () => {
      return (
        (await fetchApiQuery(
          query(() =>
            api.objects_realestate.query.ids.queryOptions({
              params: {
                slim: true,
                ids: companyIds,
                attribute: 'ownedBy',
              },
            })
          ).select((item) => item.id)
        )) ?? []
      );
    }
  );

  if (!realEstateIds?.length || realestateError) {
    return [];
  }

  return realEstateIds;
}

export const getGlobalFilterQuery = async (
  enableGlobalFilters?: boolean
): Promise<string> => {
  const { organizationId } = getSessionValues();

  let query = `&organization=${organizationId}`;

  if (!enableGlobalFilters) {
    return query;
  }

  const globalFilter = readFromLocalStorage<GlobalFilter>({
    key: GLOBAL_FILTER_KEY,
    version: 1,
    defaultValue: {
      [organizationId]: GLOBAL_FILTER_DEFAULT_VALUES,
    },
  });

  const currentFiltering = { ...globalFilter[organizationId] };
  const externalClientIds = currentFiltering.filter_external_clients || [];
  const globalCompanyIds = currentFiltering.global_companies || [];

  // Prepare concurrent lookups
  const lookupPromises: Array<Promise<string[]>> = [];

  if (externalClientIds.length > 0) {
    lookupPromises.push(fetchRealEstatesFromExternalClients(externalClientIds));
  }

  if (globalCompanyIds.length > 0) {
    lookupPromises.push(fetchRealEstatesFromCompanies(globalCompanyIds));
  }

  // Wait for all lookups to complete and merge results
  if (lookupPromises.length > 0) {
    const results = await Promise.all(lookupPromises);

    const allRealEstateIds = results.flatMap((result) => result);

    if (allRealEstateIds.length > 0) {
      const uniqueRealEstateIds = new Set([
        ...(currentFiltering.global_realestates ?? []),
        ...allRealEstateIds,
      ]);
      currentFiltering.global_realestates = Array.from(uniqueRealEstateIds);
    }
  }

  for (const key of ObjectKeys(currentFiltering)) {
    if (currentFiltering[key].length > 0 && QUERYABLE_FILTER_KEYS.has(key)) {
      query += `&${key}=${currentFiltering[key].join(',')}`;
    }
  }

  return query;
};

/**
 * The global filter resolved to a flat realestate-id set, for consumers
 * that scope by realestate ids instead of query params (the datawarehouse
 * aggregates). Mirrors getGlobalFilterQuery's semantics: companies and
 * external clients expand to their realestates, segments flatten to their
 * member realestates, and an empty union means "no global scoping"
 * (null), matching the query-param path where empty keys are omitted.
 */
export async function resolveGlobalRealestateIds(): Promise<string[] | null> {
  const { organizationId } = getSessionValues();
  const globalFilter = readFromLocalStorage<GlobalFilter>({
    key: GLOBAL_FILTER_KEY,
    version: 1,
    defaultValue: {
      [organizationId]: GLOBAL_FILTER_DEFAULT_VALUES,
    },
  });
  const current = { ...globalFilter[organizationId] };

  const lookups: Array<Promise<string[]>> = [];
  if ((current.filter_external_clients ?? []).length > 0) {
    lookups.push(
      fetchRealEstatesFromExternalClients(current.filter_external_clients)
    );
  }
  if ((current.global_companies ?? []).length > 0) {
    lookups.push(fetchRealEstatesFromCompanies(current.global_companies));
  }
  if ((current.global_segments ?? []).length > 0) {
    lookups.push(
      tryCatch(async () => {
        const { list } = await fetchAllInstances<Segment>({
          modelName: 'segment',
          filters: { id: { __in: current.global_segments.join(',') } },
          nested: ['realestates'],
          fetchAllManyRelations: true,
        });
        return list.flatMap((segment) =>
          (Array.isArray(segment.realestates) ? segment.realestates : [])
            .map((r) =>
              typeof r === 'string' ? r : ((r as { id?: string })?.id ?? null)
            )
            .filter((id): id is string => Boolean(id))
        );
      }).then(({ data }) => data ?? [])
    );
  }

  const resolved = (await Promise.all(lookups)).flat();
  const ids = new Set([...(current.global_realestates ?? []), ...resolved]);
  return ids.size > 0 ? [...ids] : null;
}

async function queryRealEstatesOwnedBy(companyIds: string[]) {
  return (
    (await fetchApiQuery(
      query(() =>
        api.objects_realestate.query.ids.queryOptions({
          params: { slim: true, ids: companyIds, attribute: 'ownedBy' },
        })
      ).select((item) => item.id)
    )) ?? []
  );
}

async function queryRealEstatesFromExternalClients(
  externalClientIds: string[]
) {
  const companyIds =
    (await fetchApiQuery(
      query(() =>
        api.organization_company.query.ids.queryOptions({
          params: {
            slim: true,
            ids: externalClientIds,
            attribute: 'managedBy',
          },
        })
      ).select((item) => item.id)
    )) ?? [];
  return companyIds.length ? queryRealEstatesOwnedBy(companyIds) : [];
}

/**
 * The global filter as Construct filters with getGlobalFilterQuery's backend
 * semantics: segments stay their own filter (the backend intersects it with
 * the realestates), while companies and external clients expand to their
 * realestates. Unlike getGlobalFilterQuery, a failed lookup throws instead of
 * silently dropping that part of the scope.
 */
export async function resolveGlobalFilterScope(): Promise<{
  globalRealestates?: string[];
  globalSegments?: string[];
}> {
  const { organizationId } = getSessionValues();
  const globalFilter = readFromLocalStorage<GlobalFilter>({
    key: GLOBAL_FILTER_KEY,
    version: 1,
    defaultValue: {
      [organizationId]: GLOBAL_FILTER_DEFAULT_VALUES,
    },
  });
  const current = { ...globalFilter[organizationId] };
  const externalClientIds = current.filter_external_clients ?? [];
  const companyIds = current.global_companies ?? [];
  const [fromClients, fromCompanies] = await Promise.all([
    externalClientIds.length
      ? queryRealEstatesFromExternalClients(externalClientIds)
      : [],
    companyIds.length ? queryRealEstatesOwnedBy(companyIds) : [],
  ]);
  const realestates = [
    ...new Set([
      ...(current.global_realestates ?? []),
      ...fromClients,
      ...fromCompanies,
    ]),
  ];
  const segments = current.global_segments ?? [];
  return {
    ...(realestates.length ? { globalRealestates: realestates } : {}),
    ...(segments.length ? { globalSegments: segments } : {}),
  };
}
